import * as Sentry from '@sentry/react'
import { Suspense, lazy, useEffect, useMemo } from 'react'
import { useIsAutoConnecting } from 'thirdweb/react'
import constants from 'app/constants'
import { useRootContext } from 'app/services/RootProvider/RootProvider'
/* eslint-disable-next-line */
import { isLocalHost, isStaging } from "app/thread";
import AppModals from 'containers/AppModals/AppModals'
import Routes from 'containers/Routes/Routes'
import Loader from 'pages/Loader/Loader'
import BurgerMenu from 'components/interface/BurgerMenu/BurgerMenu'
import Cart from 'components/interface/Cart/Cart'
import CookieConsent from 'components/interface/CookieConsent/CookieConsent'
import Header from 'components/interface/Header/Header'
import Modal from 'components/interface/Modal/Modal'
import SiteBorder from 'components/interface/SiteBorder/SiteBorder'
import Tooltip from 'components/interface/Tooltip/Tooltip'
import { isTouch } from 'lib/support'
import classNames from 'lib/util/classNames'
import styles from './App.module.scss'

/* eslint-disable-next-line */
const ThemePicker = lazy(() =>
  import(
    /* webpackChunkName: "theme-picker" */ 'app/themes/components/ThemePicker'
  )
)

if (!isLocalHost) {
  // Only enable sentry when not on local
  Sentry.init({
    dsn: constants.SENTRY_DSN,
    maxBreadcrumbs: 50,
    debug: isLocalHost
  })
}

/**
 * The entry point to the application
 *
 * @returns {React.ReactElement} the element
 */
function App () {
  const { appData, loadApplication } = useRootContext()
  const isAutoConnecting = useIsAutoConnecting()
  const classNameOutput = useMemo(
    () => classNames([styles.app, isTouch ? 'is-touch' : '']),
    []
  )

  /**
   * Effect: On mount/unmount
   */
  useEffect(() => {
    // Trigger load
    if (isAutoConnecting) {
      setTimeout(() => {
        loadApplication()
      }, 3000)
    }
  }, [isAutoConnecting])

  return (
    <div className={classNameOutput}>
      <main aria-label="Main content">
        <Loader />
        {appData && (
          <>
            <Suspense fallback={<div className="Routes" />}>
              <Routes routes={appData.routes} />
            </Suspense>
            <Header />
            <CookieConsent />
            <div className={styles.overlays}>
              <BurgerMenu />
              <Cart />
              <Modal />
              <Tooltip.Wrapper />
            </div>
            <AppModals />
          </>
        )}
        <SiteBorder />
      </main>

      {/* DEV ONLY */}
      {/* isStaging && <Suspense fallback="">
          <ThemePicker />
          </Suspense> */}
    </div>
  )
}

export default App

// Register licenses that aren't being bundled correctly.

/*!
 * The MIT License
 *
 * @license Copyright © 2010-2022 three.js authors
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
 * THE SOFTWARE.
 */
